/* Shared native enhancement owner. Forms and links retain their native fallbacks. */
(() => {
  'use strict';
  const brand = /(^|[^@/:.A-Za-z0-9_-])(erkennungsmarken\.ch)(?![A-Za-z0-9_/-]|\.[A-Za-z0-9])/giu;
  const skip = 'script,style,svg,textarea,select,option,code,pre,[contenteditable],.tf-inline-wordmark';
  const metrics=document.createElement('canvas').getContext('2d');
  const observed=new WeakSet();
  let spacingFrame=0;
  const fontFor = element => {
    const style=getComputedStyle(element);
    metrics.font=`${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    if ('fontKerning' in metrics) metrics.fontKerning=style.fontKerning;
    return style;
  };
  const neighbours = (image,block) => {
    const walker=document.createTreeWalker(block,NodeFilter.SHOW_TEXT);
    let before=null,after=null;
    while(walker.nextNode()) {
      const node=walker.currentNode;
      if (!node.textContent.trim() || image.contains(node)) continue;
      const position=image.compareDocumentPosition(node);
      if(position & Node.DOCUMENT_POSITION_PRECEDING) before=node;
      else if(position & Node.DOCUMENT_POSITION_FOLLOWING) {after=node;break;}
    }
    return {before,after};
  };
  const sameLine = (image,node,last) => {
    if(!node)return false;
    const text=node.textContent;
    const index=last?text.trimEnd().length-1:text.search(/\S/);
    if(index<0)return false;
    const range=document.createRange();range.setStart(node,index);range.setEnd(node,index+1);
    const glyph=range.getBoundingClientRect(),box=image.getBoundingClientRect();
    return Math.min(glyph.bottom,box.bottom)>Math.max(glyph.top,box.top);
  };
  const fitWordmarks = () => {
    spacingFrame=0;
    if(!metrics)return;
    document.querySelectorAll('.tf-inline-wordmark').forEach(image=>{
      const block=image.closest('p,li,figcaption,h1,h2,h3')||image.parentElement;
      if(!observed.has(block)){observed.add(block);spacingObserver?.observe(block);}
      const prefix=document.createRange();prefix.selectNodeContents(block);prefix.setEndBefore(image);
      const suffix=document.createRange();suffix.selectNodeContents(block);suffix.setStartAfter(image);
      const left=prefix.toString(),right=suffix.toString();
      const {before,after}=neighbours(image,block);
      const markStyle=fontFor(image),size=parseFloat(markStyle.fontSize),tracking=parseFloat(markStyle.letterSpacing)||0;
      const name=metrics.measureText('erkennungsmarken'),ending=metrics.measureText('ch');
      const inkStart=-name.actualBoundingBoxLeft;
      const inkEnd=ending.width-ending.actualBoundingBoxRight+tracking;
      // Shared optical gaps: tighten only the space inside the compact wordmark.
      // Canvas metrics omit CSS tracking; include its trailing advance once.
      image.style.setProperty('--tf-wordmark-dot-before',`${size*.159990385-(name.width-name.actualBoundingBoxRight+tracking)}px`);
      image.style.setProperty('--tf-wordmark-dot-after',`${size*.143377778+ending.actualBoundingBoxLeft}px`);
      image.style.setProperty('--tf-wordmark-ink-start',`${inkStart}px`);
      image.style.setProperty('--tf-wordmark-ink-end',`${inkEnd}px`);
      let start=0,end=0;
      const leftSpace=before && /\S\s+$/.test(left);
      const rightSpace=after && /^\s+\S/.test(right);
      if(leftSpace) {
        fontFor(before.parentElement);
        const word=left.trimEnd().match(/\S+$/)?.[0]||'';
        const measured=metrics.measureText(word);
        start=-(measured.width-measured.actualBoundingBoxRight)-inkStart;
      }
      if(rightSpace) {
        fontFor(after.parentElement);
        end=metrics.measureText(right.trimStart().match(/^\S+/)?.[0]||'').actualBoundingBoxLeft-inkEnd;
      }
      const style=fontFor(block);
      // Optical breathing room requested after reviewing equal measured ink
      // gaps: 1.12px in the 14px footer and 1.36px in 17px body copy.
      const breathingRoom=parseFloat(style.fontSize)*.08;
      image.style.setProperty('--tf-wordmark-space',`${metrics.measureText(' ').width+(parseFloat(style.wordSpacing)||0)}px`);
      // Native letters have side bearings. At a line edge align their ink,
      // while mid-sentence gaps retain the requested optical breathing room.
      image.style.setProperty('--tf-wordmark-start',`${-inkStart}px`);
      image.style.setProperty('--tf-wordmark-end',`${-inkEnd}px`);
      for(let pass=0;pass<2;pass++) {
        image.style.setProperty('--tf-wordmark-start',`${leftSpace&&sameLine(image,before,true)?start+breathingRoom:-inkStart}px`);
        image.style.setProperty('--tf-wordmark-end',`${rightSpace&&sameLine(image,after,false)?end+breathingRoom:-inkEnd}px`);
      }
    });
  };
  const scheduleSpacing = () => {if(!spacingFrame)spacingFrame=requestAnimationFrame(fitWordmarks);};
  const spacingObserver=window.ResizeObserver?new ResizeObserver(scheduleSpacing):null;
  const makeWordmark = () => {
    const image=document.createElement('span');
    image.className='tf-inline-wordmark tf-inline-wordmark--type';
    image.setAttribute('role','img');image.setAttribute('aria-label','erkennungsmarken.ch');
    for(const [part,text] of [['name','erkennungsmarken'],['dot',''],['suffix','ch']]){
      const span=document.createElement('span');span.className='tf-inline-wordmark__'+part;span.textContent=text;
      if(part==='dot')span.setAttribute('aria-hidden','true');
      image.append(span);
    }
    return image;
  };
  const render = scope => {
    const targets = scope.matches?.('#MainContent,.tf-footer') || scope.closest?.('#MainContent,.tf-footer') ? [scope] : Array.from(scope.querySelectorAll?.('#MainContent,.tf-footer') || []);
    for (const target of targets) {
      target.querySelectorAll('.tf-inline-wordmark:not(.tf-inline-wordmark--type)').forEach(old=>old.replaceWith(makeWordmark()));
      const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (node.parentElement?.closest(skip) || !brand.test(node.textContent)) { brand.lastIndex = 0; continue; }
        brand.lastIndex = 0;
        nodes.push(node);
      }
      for (const node of nodes) {
        const value = node.textContent;
        const fragment = document.createDocumentFragment();
        let cursor = 0;
        for (const match of value.matchAll(brand)) {
          const start = match.index + match[1].length;
          fragment.append(document.createTextNode(value.slice(cursor, start)));
          const image=makeWordmark();
          fragment.append(image);
          cursor = start + match[2].length;
        }
        fragment.append(document.createTextNode(value.slice(cursor)));
        node.replaceWith(fragment);
      }
    }
  };
  render(document);
  fitWordmarks();
  window.addEventListener('resize',scheduleSpacing,{passive:true});
  document.fonts?.ready?.then(scheduleSpacing);
  document.fonts?.addEventListener('loadingdone',scheduleSpacing);
  document.addEventListener('shopify:section:load', event => {render(event.target);scheduleSpacing();});
  document.addEventListener('shopify:section:unload',event=>event.target.querySelectorAll('.tf-inline-wordmark').forEach(image=>spacingObserver?.unobserve(image.closest('p,li,figcaption,h1,h2,h3')||image.parentElement)));
})();
/* Search hints use the longest complete wording that fits the real field. */
(() => {
  'use strict';
  const context = document.createElement('canvas').getContext('2d');
  if (!context) return;
  const compact = new WeakMap();
  const selector = 'input[data-tf-placeholder-full]';
  const fit = input => {
    if (!input.clientWidth) return;
    const style = getComputedStyle(input);
    context.font = style.font || `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const tracking = parseFloat(style.letterSpacing) || 0;
    // Reserve room for Safari's native search affordance and clear control.
    const available = input.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 36;
    const choices = [input.dataset.tfPlaceholderFull, compact.get(input), input.dataset.tfPlaceholderMin];
    const text = choices.find(value => value && context.measureText(value).width + tracking * Math.max(0,value.length-1) <= available) || choices[2];
    if (text && input.placeholder !== text) input.placeholder = text;
  };
  const observer = window.ResizeObserver ? new ResizeObserver(entries => entries.forEach(entry => fit(entry.target))) : null;
  const bind = scope => scope.querySelectorAll(selector).forEach(input => {
    if (compact.has(input)) return;
    compact.set(input,input.placeholder);observer?.observe(input);fit(input);
  });
  const fitAll = () => document.querySelectorAll(selector).forEach(fit);
  bind(document);
  window.addEventListener('resize',fitAll,{passive:true});
  document.fonts?.ready?.then(fitAll);
  document.fonts?.addEventListener('loadingdone',fitAll);
  document.addEventListener('shopify:section:load',event => bind(event.target));
  document.addEventListener('shopify:section:unload',event => event.target.querySelectorAll(selector).forEach(input => observer?.unobserve(input)));
})();
(() => {
  'use strict';

  // Keep pointer-opened utilities neutral while retaining explicit keyboard focus.
  document.addEventListener('pointerdown', () => { document.documentElement.dataset.tfInput = 'pointer'; }, { passive:true, capture:true });
  document.addEventListener('keydown', () => { document.documentElement.dataset.tfInput = 'keyboard'; }, true);

  // One scroll-intent owner; keep focused utilities and open native dialogs reachable.
  const bindHeader = header => {
    if (!header || header.dataset.tfScrollBound) return;
    header.dataset.tfScrollBound = 'true';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let previous = Math.max(0, window.scrollY), distance = 0, direction = 0, raf = 0;
    const show = () => header.removeAttribute('data-tf-header-hidden');
    const protectedState = () => header.contains(document.activeElement) || document.querySelector('dialog.tf-dialog[open]') || document.body.style.position === 'fixed';
    const update = () => {
      raf = 0;
      const current = Math.max(0, window.scrollY);
      const delta = current - previous;
      if (current <= header.offsetHeight || protectedState()) { previous = current; distance = 0; direction = 0; show(); return; }
      if (Math.abs(delta) < 1) return;
      previous = current;
      const next = Math.sign(delta);
      distance = next === direction ? distance + Math.abs(delta) : Math.abs(delta);
      direction = next;
      if (distance >= (next > 0 ? 24 : 8)) {
        if (next > 0) header.setAttribute('data-tf-header-hidden', ''); else show();
        distance = 0;
      }
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
    const reset = () => { previous = Math.max(0, window.scrollY); distance = 0; direction = 0; show(); };
    header.setAttribute('data-tf-scroll-header', '');
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', reset, { passive: true });
    window.addEventListener('pageshow', reset);
    header.addEventListener('focusin', show);
    reduce.addEventListener('change', reset);
    header.tfScrollCleanup = () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', reset);
      window.removeEventListener('pageshow', reset);
      header.removeEventListener('focusin', show);
      reduce.removeEventListener('change', reset);
      delete header.dataset.tfScrollBound;
    };
  };
  bindHeader(document.querySelector('.tf-header'));
  document.addEventListener('shopify:section:load', event => bindHeader(event.target.querySelector('.tf-header')));
  document.addEventListener('shopify:section:unload', event => event.target.querySelector('.tf-header')?.tfScrollCleanup?.());

  const bindContactIntro = scope => {
    const intro = scope.querySelector?.('.tf-contact-intro');
    if (!intro || intro.dataset.tfStickyBound) return;
    intro.dataset.tfStickyBound = 'true';
    const update = () => {
      intro.classList.remove('tf-contact-intro--sticky');
      const offset = Math.ceil(document.querySelector('.tf-header')?.getBoundingClientRect().height || 100) + 24;
      intro.style.setProperty('--tf-contact-sticky-top', `${offset}px`);
      if (window.innerWidth >= 961 && intro.getBoundingClientRect().height + offset + 24 <= window.innerHeight) intro.classList.add('tf-contact-intro--sticky');
    };
    let frame = 0;
    const schedule = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; update(); }); };
    window.addEventListener('resize', schedule, { passive:true });
    document.fonts?.ready?.then(schedule);
    if (window.ResizeObserver) new ResizeObserver(schedule).observe(intro);
    schedule();
  };
  bindContactIntro(document);
  document.addEventListener('shopify:section:load', event => bindContactIntro(event.target));

  let menuReturn;
  let menuScroll;
  let menuBodyStyles;
  let menuCloseTimer;
  const closeMenu = dialog => {
    if (!dialog?.open) return;
    if (!dialog.matches('.tf-menu')) { dialog.close(); return; }
    dialog.classList.remove('tf-menu--visible');
    window.clearTimeout(menuCloseTimer);
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) dialog.close();
    else menuCloseTimer = window.setTimeout(() => { if (dialog.open) dialog.close(); }, 260);
  };
  const lockMenu = () => {
    menuScroll = { left: window.scrollX, top: window.scrollY };
    menuBodyStyles = ['position', 'top', 'left', 'width'].map(key => [key, document.body.style[key]]);
    document.body.style.position = 'fixed';
    document.body.style.top = `${-menuScroll.top}px`;
    document.body.style.left = `${-menuScroll.left}px`;
    document.body.style.width = '100%';
  };
  const unlockMenu = () => {
    if (!menuScroll) return;
    menuBodyStyles.forEach(([key, value]) => { document.body.style[key] = value; });
    const previous = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(menuScroll.left, menuScroll.top);
    document.documentElement.style.scrollBehavior = previous;
    menuScroll = null;
  };
  document.addEventListener('click', event => {
    const open = event.target.closest('[data-tf-menu-open],[data-tf-dialog-open]');
    const close = event.target.closest('[data-tf-menu-close],[data-tf-dialog-close]');
    const dialog = open ? document.getElementById(open.dataset.tfDialogOpen || 'TfMenu') : close?.closest('dialog');
    if (open && dialog && !dialog.open && typeof dialog.showModal === 'function') {
      window.clearTimeout(menuCloseTimer);
      menuReturn = open;
      dialog.scrollTop = 0;
      lockMenu();
      dialog.showModal();
      if (event.detail !== 0) {
        dialog.tabIndex = -1;
        dialog.focus({ preventScroll:true });
      }
      if (dialog.matches('.tf-menu')) requestAnimationFrame(() => { if (dialog.open) dialog.classList.add('tf-menu--visible'); });
    }
    if (close && dialog) closeMenu(dialog);
  });
  document.addEventListener('cancel', event => {
    if (event.target.matches('dialog.tf-menu')) { event.preventDefault(); closeMenu(event.target); }
  }, true);
  document.addEventListener('close', event => {
    if (event.target.matches('dialog.tf-dialog')) { event.target.classList.remove('tf-menu--visible'); unlockMenu(); menuReturn?.focus({ preventScroll: true }); }
  }, true);
  document.addEventListener('change', event => {
    if (event.target.matches('[data-tf-language],[data-tf-sort]')) {
      const form = event.target.form;
      if (form?.requestSubmit) form.requestSubmit();
    }
  });
  window.matchMedia('(min-width: 961px)').addEventListener('change', event => {
    const dialog = document.getElementById('TfMenu');
    if (event.matches && dialog?.open) closeMenu(dialog);
  });
  const applyFilter = (input, updateLocation = false) => {
    const section = input.closest('.tf-section');
    if (!section) return;
    const needle = input.value.toLocaleLowerCase();
    let count = 0;
    section.querySelectorAll('[data-tf-filter-item]').forEach(item => {
      item.hidden = !item.dataset.tfSearch.toLocaleLowerCase().includes(needle);
      if (!item.hidden) count += 1;
    });
    const empty = section.querySelector('[data-tf-filter-empty]');
    if (empty) empty.hidden = count !== 0;
    section.querySelectorAll('[data-tf-filter-persist]').forEach(field => {
      field.value = input.value;
      field.disabled = input.value === '';
    });
    if (updateLocation) {
      const url = new URL(window.location.href);
      if (input.value === '') url.searchParams.delete('tf_filter');
      else url.searchParams.set('tf_filter', input.value);
      window.history.replaceState(window.history.state, '', url);
      paginationQuery(section);
    }
  };
  const filters = () => {
    const query = new URLSearchParams(window.location.search);
    document.querySelectorAll('[data-tf-filter-control]').forEach(control => {
      const input = control.querySelector('[data-tf-filter-input]');
      if (!input) return;
      if (!input.hasAttribute('data-tf-filter-bound')) {
        if (query.has('tf_filter')) input.value = query.get('tf_filter');
        input.setAttribute('data-tf-filter-bound', '');
      }
      applyFilter(input);
      control.hidden = false;
    });
  };
  // A03: the real select remains the sole submitted sort owner.
  const sortStates = new WeakMap();
  let openSort = null;
  const bindSortControl = root => {
    if (sortStates.has(root)) return;
    const select = root.querySelector('select[data-tf-sort]');
    const enhanced = root.querySelector('[data-tf-sort-enhanced]');
    const trigger = root.querySelector('[data-tf-sort-trigger]');
    const list = root.querySelector('[data-tf-sort-list]');
    const valueLabel = root.querySelector('[data-tf-sort-value-label]');
    const label = select?.form?.querySelector('[data-tf-sort-label]');
    if (!select || select.name !== 'sort_by' || !select.form || !enhanced || !trigger || !list || !valueLabel || !label || !select.id || !trigger.id || !list.id || select.disabled) return;
    const options = Array.from(select.options);
    const values = options.map(option => option.value);
    if (options.length < 2 || options.length > 4 || new Set(values).size !== options.length || values.some(value => !['title-ascending', 'title-descending', 'price-ascending', 'price-descending'].includes(value)) || options.some(option => !option.textContent || option.disabled) || !values.includes(select.value) || select.value !== root.dataset.tfSortCurrent) return;
    let active = values.indexOf(select.value), opened = false, prefix = '', prefixTimer;
    const originalHidden = select.hidden;
    const originalFor = label.htmlFor;
    const nodes = options.map((option, index) => {
      const node = document.createElement('li');
      node.id = `${list.id}-${index}`;
      node.className = 'tf-sort-control__option';
      node.setAttribute('role', 'option');
      node.dataset.tfSortValue = option.value;
      node.textContent = option.textContent;
      return node;
    });
    const activate = index => {
      active = Math.max(0, Math.min(nodes.length - 1, index));
      nodes.forEach((node, i) => node.toggleAttribute('data-tf-sort-active', i === active));
      list.setAttribute('aria-activedescendant', nodes[active].id);
      if (opened) nodes[active].scrollIntoView({ block: 'nearest', behavior: 'auto' });
    };
    const sync = () => {
      const index = values.indexOf(select.value);
      if (index < 0) return false;
      valueLabel.textContent = options[index].textContent;
      nodes.forEach((node, i) => node.setAttribute('aria-selected', String(i === index)));
      return true;
    };
    const close = (returnFocus = false) => {
      opened = false;
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      nodes.forEach(node => node.removeAttribute('data-tf-sort-active'));
      prefix = '';
      if (prefixTimer !== undefined) window.clearTimeout(prefixTimer);
      if (openSort?.root === root) openSort = null;
      if (returnFocus && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
    const open = index => {
      if (!root.isConnected || select.disabled || !sync()) return;
      openSort?.close();
      opened = true;
      openSort = { root, close };
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      activate(index ?? values.indexOf(select.value));
      list.focus({ preventScroll: true });
    };
    const commit = index => {
      const option = options[index];
      if (!option || option.disabled || select.disabled || !Array.from(select.options).includes(option)) { close(true); return; }
      const before = select.value;
      select.value = option.value;
      if (select.value !== option.value) { close(true); return; }
      sync();
      close(true);
      if (before !== select.value) select.dispatchEvent(new Event('change', { bubbles: true }));
    };
    const onTriggerClick = () => opened ? close(true) : open();
    const onTriggerKey = event => {
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      open(event.key === 'Home' ? 0 : event.key === 'End' ? nodes.length - 1 : undefined);
    };
    const onListKey = event => {
      if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        activate(event.key === 'Home' ? 0 : event.key === 'End' ? nodes.length - 1 : active + (event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault(); commit(active);
      } else if (event.key === 'Escape') {
        event.preventDefault(); close(true);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        const char = event.key.toLocaleLowerCase();
        const repeated = prefix !== '' && Array.from(prefix).every(value => value === char);
        prefix = repeated ? char : prefix + char;
        if (prefixTimer !== undefined) window.clearTimeout(prefixTimer);
        prefixTimer = window.setTimeout(() => { prefix = ''; }, 700);
        const matches = options.map((option, index) => option.textContent.toLocaleLowerCase().startsWith(prefix) ? index : -1).filter(index => index >= 0);
        if (matches.length) activate((prefix.length === 1 ? matches.find(index => index > active) : matches[0]) ?? matches[0]);
      }
    };
    const onListClick = event => {
      const node = event.target.closest('[data-tf-sort-value]');
      const index = nodes.indexOf(node);
      if (index >= 0) commit(index);
    };
    const onFocusOut = () => queueMicrotask(() => {
      if (opened && (!root.contains(document.activeElement) || document.activeElement === trigger)) close();
    });
    const onChange = () => { sync(); close(); };
    list.replaceChildren(...nodes);
    if (!sync()) return;
    trigger.addEventListener('click', onTriggerClick);
    trigger.addEventListener('keydown', onTriggerKey);
    list.addEventListener('keydown', onListKey);
    list.addEventListener('click', onListClick);
    root.addEventListener('focusout', onFocusOut);
    select.addEventListener('change', onChange);
    select.hidden = true;
    label.htmlFor = trigger.id;
    enhanced.hidden = false;
    root.dataset.tfSortEnhanced = 'true';
    sortStates.set(root, () => {
      close();
      trigger.removeEventListener('click', onTriggerClick);
      trigger.removeEventListener('keydown', onTriggerKey);
      list.removeEventListener('keydown', onListKey);
      list.removeEventListener('click', onListClick);
      root.removeEventListener('focusout', onFocusOut);
      select.removeEventListener('change', onChange);
      select.hidden = originalHidden;
      label.htmlFor = originalFor;
      enhanced.hidden = true;
      delete root.dataset.tfSortEnhanced;
      sortStates.delete(root);
    });
  };
  const sortControls = (scope = document) => scope.querySelectorAll('[data-tf-sort-control]').forEach(root => {
    try { bindSortControl(root); } catch { /* Keep the actual native select on unsupported markup. */ }
  });
  document.addEventListener('pointerdown', event => { if (openSort && !openSort.root.contains(event.target)) openSort.close(); });
  const paginationQuery = (scope = document) => scope.querySelectorAll('[data-tf-pagination]').forEach(region => {
    try {
      const current = new URL(window.location.href);
      const keys = ['sort_by', 'tf_filter', 'q', 'type', 'options[prefix]'];
      for (const link of region.querySelectorAll('[data-tf-pagination-link]')) {
        const url = new URL(link.href, current);
        if (url.origin !== current.origin || url.pathname !== current.pathname) continue;
        for (const key of keys) {
          if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
          else if (key === 'tf_filter') url.searchParams.delete(key);
        }
        link.href = url.href;
      }
    } catch { /* Native pagination links remain usable. */ }
  });
  document.addEventListener('shopify:section:load', event => { sortControls(event.target); paginationQuery(event.target); });
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-tf-sort-control]').forEach(root => sortStates.get(root)?.()));
  // End A03/A08: the existing collection/filter/order engines below stay native.
  // Native cards remain usable until a complete localized order can be committed.
  const collectionOrderStates = new WeakMap();
  const collectionOrderKey = value => {
    const url = new URL(value, window.location.href);
    url.searchParams.delete('tf_filter');
    url.hash = '';
    return url.href;
  };
  const readCollectionOrderMeta = (grid, sourceUrl = new URL(window.location.href)) => {
    const data = grid.dataset;
    const number = key => {
      const value = Number(data[key]);
      if (!Number.isSafeInteger(value) || value < 1) throw new Error('Invalid collection metadata');
      return value;
    };
    const meta = {
      id: data.tfCollectionId,
      handle: data.tfCollectionHandle,
      section: data.tfSectionId,
      locale: data.tfLocale,
      sort: data.tfSort,
      page: number('tfPage'),
      pages: number('tfPages'),
      size: number('tfPageSize'),
      total: number('tfTotal'),
      param: data.tfPageParam
    };
    const syntheticAll = meta.id === '' && meta.handle === 'all' && sourceUrl.origin === new URL(window.location.href).origin && sourceUrl.pathname.endsWith('/collections/all');
    if ((!/^[1-9][0-9]*$/.test(meta.id || '') && !syntheticAll) || !meta.handle || !meta.section || !meta.locale || !/^[a-z0-9_]+$/i.test(meta.param || '') || meta.size > 250 || meta.total > 250 || meta.pages !== Math.ceil(meta.total / meta.size) || meta.page > meta.pages) throw new Error('Invalid collection coverage');
    return meta;
  };
  const readCollectionOrderCards = grid => Array.from(grid.children).map(node => {
    const id = node.dataset.tfProductId;
    const handle = node.dataset.tfProductHandle;
    const title = node.dataset.tfSortTitle;
    if (!node.matches('[data-tf-filter-item]') || !/^[1-9][0-9]*$/.test(id || '') || !handle || !title || node.querySelector('h3')?.textContent !== title) throw new Error('Invalid collection card');
    return { node, id, handle, title };
  });
  const compareCollectionOrderCards = locale => {
    const collator = new Intl.Collator(locale, { usage: 'sort', sensitivity: 'base', numeric: false, ignorePunctuation: false });
    return (a, b) => collator.compare(a.title, b.title) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  };
  const orderCollection = async grid => {
    if (collectionOrderStates.has(grid)) return;
    const state = {};
    const cleanupFocus = () => {
      if (state.focusHandler) grid.removeEventListener('focusout', state.focusHandler);
      if (state.focusTimer !== undefined) window.clearTimeout(state.focusTimer);
      state.focusHandler = undefined;
      state.focusTimer = undefined;
      state.deferred = false;
    };
    state.cleanupFocus = cleanupFocus;
    collectionOrderStates.set(grid, state);
    grid.dataset.tfOrderState = 'native';
    let timer;
    try {
      const currentUrl = new URL(window.location.href);
      const meta = readCollectionOrderMeta(grid, currentUrl);
      const locale = document.documentElement.lang;
      if (!['title-ascending', 'title-descending'].includes(meta.sort) || !locale || locale.toLowerCase() !== meta.locale.toLowerCase() || typeof Intl.Collator !== 'function' || typeof AbortController !== 'function') return;
      const key = collectionOrderKey(currentUrl);
      const section = grid.closest('.tf-section');
      const pages = new Array(meta.pages);
      const currentCards = readCollectionOrderCards(grid);
      const expectedCount = page => Math.min(meta.size, meta.total - (page - 1) * meta.size);
      if (currentCards.length !== expectedCount(meta.page)) throw new Error('Incomplete current page');
      pages[meta.page - 1] = currentCards;
      state.controller = new AbortController();
      state.controller.signal.addEventListener('abort', cleanupFocus, { once: true });
      timer = window.setTimeout(() => state.controller.abort(), 10000);
      await Promise.all(Array.from({ length: meta.pages }, async (_, index) => {
        const page = index + 1;
        if (page === meta.page) return;
        const url = new URL(currentUrl);
        url.searchParams.set(meta.param, String(page));
        url.searchParams.set('section_id', meta.section);
        const response = await fetch(url, { credentials: 'same-origin', signal: state.controller.signal });
        const responseUrl = new URL(response.url);
        if (!response.ok || responseUrl.origin !== currentUrl.origin || responseUrl.pathname !== currentUrl.pathname) throw new Error('Invalid native page response');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const matches = parsed.querySelectorAll('[data-tf-collection-grid]');
        if (matches.length !== 1) throw new Error('Missing native collection grid');
        const other = readCollectionOrderMeta(matches[0], responseUrl);
        if (other.id !== meta.id || other.handle !== meta.handle || other.section !== meta.section || other.locale !== meta.locale || other.sort !== meta.sort || other.total !== meta.total || other.pages !== meta.pages || other.size !== meta.size || other.param !== meta.param || other.page !== page) throw new Error('Mismatched native page');
        const cards = readCollectionOrderCards(matches[0]);
        if (cards.length !== expectedCount(page)) throw new Error('Incomplete native page');
        pages[index] = cards;
      }));
      const cards = pages.flat();
      if (cards.length !== meta.total || new Set(cards.map(card => card.id)).size !== meta.total || new Set(cards.map(card => card.handle)).size !== meta.total) throw new Error('Incomplete unique catalogue');
      cards.sort(compareCollectionOrderCards(locale));
      if (meta.sort === 'title-descending') cards.reverse();
      const start = (meta.page - 1) * meta.size;
      const shown = cards.slice(start, start + meta.size);
      const commitCurrent = () => {
        const select = section?.querySelector('select[data-tf-sort]');
        if (state.controller.signal.aborted || !grid.isConnected || collectionOrderStates.get(grid) !== state || collectionOrderKey(window.location.href) !== key || document.documentElement.lang.toLowerCase() !== locale.toLowerCase() || (select && select.value !== meta.sort)) return 'native';
        // Keep a card in place while the visitor is using it with the keyboard.
        if (grid.contains(document.activeElement)) return 'focused';
        const fragment = document.createDocumentFragment();
        shown.forEach(card => fragment.appendChild(card.node.cloneNode(true)));
        grid.replaceChildren(fragment);
        grid.dataset.tfOrderState = 'localized';
        const input = section?.querySelector('[data-tf-filter-input]');
        if (input) applyFilter(input);
        return 'localized';
      };
      if (commitCurrent() === 'focused') {
        state.deferred = true;
        state.focusHandler = () => {
          if (state.focusTimer !== undefined) return;
          state.focusTimer = window.setTimeout(() => {
            state.focusTimer = undefined;
            try {
              if (commitCurrent() === 'focused') return;
            } catch {
              // Retain native browsing if a deferred state cannot be committed.
            }
            cleanupFocus();
            state.controller.abort();
          }, 0);
        };
        grid.addEventListener('focusout', state.focusHandler);
      }
    } catch {
      // Preserve the complete original native grid and its ordinary links.
    } finally {
      if (timer !== undefined) window.clearTimeout(timer);
      if (!state.deferred) state.controller?.abort();
    }
  };
  const collectionOrders = () => document.querySelectorAll('[data-tf-collection-grid]').forEach(orderCollection);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll?.('[data-tf-collection-grid]').forEach(grid => {
      const state = collectionOrderStates.get(grid);
      state?.controller?.abort();
      state?.cleanupFocus?.();
      collectionOrderStates.delete(grid);
    });
  });
  document.addEventListener('input', event => {
    if (!event.target.matches('[data-tf-filter-input]')) return;
    applyFilter(event.target, true);
  });
  document.addEventListener('click', event => {
    const clear = event.target.closest('[data-tf-filter-clear]');
    if (!clear) return;
    const input = clear.closest('.tf-section').querySelector('[data-tf-filter-input]');
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.focus();
  });
  const orderContactProductOptions = select => {
    try {
      const locale = document.documentElement.lang.toLowerCase();
      if ((locale !== 'fr' && locale !== 'it') || typeof Intl.Collator !== 'function') return;
      const options = Array.from(select.options);
      if (options.length < 2 || options[0].value !== '' || options[0].dataset.tfHandle) return;
      const products = options.slice(1).map(node => ({ node, title: node.textContent, id: node.dataset.tfHandle }));
      if (products.some(item => !item.title || !item.id) || new Set(products.map(item => item.id)).size !== products.length) return;
      const selected = options[select.selectedIndex] || null;
      products.sort((a, b) => (a.id === 'original-erkennungsmarke-65' ? -1 : b.id === 'original-erkennungsmarke-65' ? 1 : compareCollectionOrderCards(locale)(a, b)));
      const ordered = [options[0], ...products.map(item => item.node)];
      if (ordered.every((node, index) => node === options[index])) return;
      select.replaceChildren(...ordered);
      select.selectedIndex = selected ? ordered.indexOf(selected) : -1;
    } catch {
      // Retain native option order and product/gold prefill if enhancement is unavailable.
    }
  };
  const contactProduct = document.querySelector('[data-tf-contact-product]');
  if (contactProduct) {
    orderContactProductOptions(contactProduct);
    const query = new URLSearchParams(window.location.search);
    const product = query.get('product');
    if (product) {
      const option = Array.from(contactProduct.options).find(item => item.dataset.tfHandle === product);
      if (option) contactProduct.value = option.value;
    }
    if (query.get('enquiry') === 'gold' || query.get('thema') === 'goldlegierung') {
      const subject = document.querySelector('[data-tf-contact-subject]');
      const gold = subject?.querySelector('[data-tf-gold]');
      if (gold) subject.value = gold.value;
    }
  }
  filters();
  sortControls();
  paginationQuery();
  collectionOrders();
  document.addEventListener('shopify:section:load', filters);
  document.addEventListener('shopify:section:load', collectionOrders);

})();

/* Explicit guide-to-Original continuation: separate key/schema from the language handoff below. */
(() => {
  'use strict';
  if (window.tfGuideDraft) return;
  const storageKey = 'tf:guide-original-draft:v1';
  const lifetime = 120000;
  const handle = 'original-erkennungsmarke-65';
  const locale = value => {
    const code = typeof value === 'string' ? value.toLowerCase().split('-')[0] : '';
    return ['de', 'fr', 'it'].includes(code) ? code : null;
  };
  const numericID = value => typeof value === 'string' && /^[1-9][0-9]*$/.test(value);
  const clear = () => {
    try { window.sessionStorage.removeItem(storageKey); return true; }
    catch { return false; }
  };
  const identity = (root, requireAvailable = true) => {
    const data = root?.dataset;
    const code = locale(data?.tfLocale);
    if (!data || !numericID(data.tfProductId) || !numericID(data.tfVariantId) || data.tfHandle !== handle || data.tfPattern !== 'classic' || data.tfCapped !== 'false' || !['true', 'false'].includes(data.tfAvailable) || (requireAvailable && data.tfAvailable !== 'true') || !code || code !== locale(document.documentElement.lang)) throw new Error('Invalid guide destination');
    const url = new URL(data.tfTargetUrl, window.location.href);
    const suffix = `/products/${handle}`;
    if (url.origin !== window.location.origin || url.search || url.hash || !url.pathname.endsWith(suffix)) throw new Error('Invalid Original route');
    const base = url.pathname.slice(0, -suffix.length);
    if (base !== '' && !/^\/(de|fr|it)(-[a-z]{2})?$/u.test(base)) throw new Error('Invalid locale route');
    if ((base && locale(base.slice(1)) !== code) || (!base && code !== 'de')) throw new Error('Mismatched locale route');
    return { locale: code, product_id: data.tfProductId, variant_id: data.tfVariantId, target_path: url.pathname, source_path: `${base}/pages/gravur` };
  };
  const save = (root, text) => {
    if (!clear()) return false;
    try {
      const destination = describe(root);
      if (!destination || typeof text !== 'string' || !/\S/u.test(text)) return false;
      const record = {
        schema: 1, source: 'inscription-guide', captured_at: Date.now(), origin: window.location.origin,
        ...destination, pattern: 'classic', capped: 'false', mode: 'text', text
      };
      const raw = JSON.stringify(record);
      window.sessionStorage.setItem(storageKey, raw);
      if (window.sessionStorage.getItem(storageKey) !== raw) throw new Error('Draft was not retained');
      return true;
    } catch { clear(); return false; }
  };
  const describe = (root, requireAvailable = true) => {
    try {
      if (!root?.matches('[data-tf-inscription-preview]')) return null;
      const destination = identity(root, requireAvailable);
      const link = root.querySelector('[data-tf-preview-continue]');
      if (window.location.pathname !== destination.source_path || !link || new URL(link.href, window.location.href).href !== `${window.location.origin}${destination.target_path}`) return null;
      return destination;
    } catch { return null; }
  };
  const take = () => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw === null) return null;
      window.sessionStorage.removeItem(storageKey);
      if (window.sessionStorage.getItem(storageKey) !== null) return null;
      return JSON.parse(raw);
    } catch { return null; }
  };
  const matchesRecord = (record, target) => {
    const age = Date.now() - record?.captured_at;
    if (!record || record.schema !== 1 || record.source !== 'inscription-guide' || !Number.isSafeInteger(record.captured_at) || age < 0 || age > lifetime || record.origin !== window.location.origin || record.pattern !== 'classic' || record.capped !== 'false' || record.mode !== 'text' || typeof record.text !== 'string' || !/\S/u.test(record.text)) return false;
    if (Object.keys(target).some(key => record[key] !== target[key]) || window.location.pathname !== target.target_path) return false;
    try {
      const from = new URL(document.referrer);
      return from.origin === record.origin && from.pathname === target.source_path;
    } catch { return false; }
  };
  const showRecovery = (root, record) => {
    const region = root.querySelector('[data-tf-guide-recovery]');
    const input = region?.querySelector('[data-tf-guide-recovery-input]');
    if (!region || !input) return;
    input.value = record.text;
    region.hidden = false;
    root.dataset.tfGuideRestore = 'recovery';
  };
  const restore = () => {
    const record = take();
    if (!record) return;
    const roots = document.querySelectorAll('[data-tf-guide-target]');
    if (roots.length !== 1) return;
    const root = roots[0];
    let target;
    try { target = identity(root, false); } catch { return; }
    if (!matchesRecord(record, target)) return;
    let textForm, blankForm, input, textDetails, blankDetails, before;
    try {
      if (root.dataset.tfAvailable !== 'true') throw new Error('Original is unavailable');
      const forms = Array.from(root.querySelectorAll('[data-tf-engraving-form][data-tf-product-id]'));
      if (forms.length !== 2 || forms.some(form => form.dataset.tfCartKey || form.closest('[data-tf-cart-properties]') || form.dataset.tfProductId !== target.product_id)) throw new Error('Mixed guide form');
      textForm = forms.find(form => form.dataset.tfMode === 'text');
      blankForm = forms.find(form => form.dataset.tfMode === 'blank');
      if (!textForm || !blankForm) throw new Error('Missing front choices');
      for (const form of forms) {
        const variants = form.querySelectorAll('input[name="id"]');
        const patterns = form.querySelectorAll('input[name="properties[_tf_pattern]"]');
        const caps = form.querySelectorAll('input[name="properties[_tf_capped]"]');
        if (variants.length !== 1 || variants[0].value !== target.variant_id || patterns.length !== 1 || patterns[0].value !== 'classic' || caps.length !== 1 || caps[0].value !== 'false') throw new Error('Changed Original schema');
      }
      const fields = textForm.querySelectorAll('[data-tf-field]');
      input = fields[0];
      if (fields.length !== 1 || input.tagName !== 'TEXTAREA' || input.dataset.tfKind !== 'front' || blankForm.querySelectorAll('[data-tf-field]').length !== 0) throw new Error('Not the uncapped Original front');
      textDetails = textForm.closest('details.tf-config-mode');
      blankDetails = blankForm.closest('details.tf-config-mode');
      const review = textForm.querySelector('[data-tf-review-button]');
      const confirm = textForm.querySelector('[data-tf-confirm]');
      const add = textForm.querySelector('[data-tf-add]');
      if (!textDetails || !blankDetails || !review || review.hidden || !confirm || !add) throw new Error('Exact review owner unavailable');
      before = { value: input.value, textOpen: textDetails.open, blankOpen: blankDetails.open };
      input.value = record.text;
      blankDetails.open = false;
      textDetails.open = true;
      if (input.value !== record.text || !textDetails.open || blankDetails.open) throw new Error('Draft could not be restored');
      // Dispatch the existing commerce owner; quantity and all review approvals stay local.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      confirm.checked = false;
      add.disabled = true;
      root.dataset.tfGuideRestore = 'restored';
    } catch {
      if (before) {
        try { input.value = before.value; } catch {}
        try { textDetails.open = false; blankDetails.open = before.blankOpen; textDetails.open = before.textOpen; } catch {}
        // A failed attempt must not leave a stale exact-value approval usable.
        try { input.dispatchEvent(new Event('input', { bubbles: true })); } catch {}
      }
      showRecovery(root, record);
    }
  };
  window.tfGuideDraft = Object.freeze({ save, clear, describe });
  if (document.readyState === 'complete') restore();
  else document.addEventListener('DOMContentLoaded', restore, { once: true });
  window.addEventListener('pageshow', event => { if (event.persisted) restore(); });
})();

/* External destinations use the same safe new-tab behavior in every content owner. */
(() => {
  'use strict';
  const externalLink = link => {
    if (!link) return;
    try {
      const url = new URL(link.href, window.location.href);
      if (!['http:', 'https:'].includes(url.protocol) || url.origin === window.location.origin) return;
      link.target = '_blank';
      link.relList.remove('opener');
      link.relList.add('noopener', 'noreferrer');
    } catch { /* Leave non-URL native actions alone. */ }
  };
  const bind = scope => scope.querySelectorAll('a[href]').forEach(externalLink);
  bind(document);
  document.addEventListener('shopify:section:load', event => bind(event.target));
  document.addEventListener('click', event => externalLink(event.target.closest('a[href]')), true);
})();

/* One short-lived handoff for explicit same-product native language changes. */
(() => {
  'use strict';
  const storageKey = 'tf:pdp-language-draft:v1';
  const lifetime = 120000;
  const languageForm = document.getElementById('TfHeaderLanguage');
  const language = languageForm?.querySelector('[data-tf-language]');
  const locale = value => {
    const code = typeof value === 'string' ? value.toLowerCase().split('-')[0] : '';
    return ['de', 'fr', 'it'].includes(code) ? code : null;
  };
  const initialLocale = locale(language?.value);
  let armed = null;
  let savedOnThisPage = false;
  const clear = () => {
    try { window.sessionStorage.removeItem(storageKey); return true; }
    catch { return false; }
  };
  const take = () => {
    try {
      const raw = window.sessionStorage.getItem(storageKey);
      if (raw === null) return null;
      window.sessionStorage.removeItem(storageKey);
      return JSON.parse(raw);
    } catch { return null; }
  };
  const numericID = value => typeof value === 'string' && /^[1-9][0-9]*$/.test(value);
  const fieldKey = field => `${field.kind}:${field.slot || ''}`;
  const readProduct = () => {
    const forms = Array.from(document.querySelectorAll('[data-tf-engraving-form][data-tf-product-id]'))
      .filter(form => !form.dataset.tfCartKey && !form.closest('[data-tf-cart-properties]'));
    if (forms.length !== 2) throw new Error('No single engraving product');
    const modes = forms.map(form => {
      const metadata = key => {
        const fields = form.querySelectorAll(`input[name="properties[${key}]"]`);
        if (fields.length !== 1) throw new Error('Missing engraving metadata');
        return fields[0].value;
      };
      const variants = form.querySelectorAll('input[name="id"]');
      const quantities = form.querySelectorAll('[data-tf-quantity]');
      const quantity = quantities[0];
      const details = form.closest('details.tf-config-mode');
      const mode = form.dataset.tfMode;
      const productID = form.dataset.tfProductId;
      if (!numericID(productID) || variants.length !== 1 || !numericID(variants[0].value) || quantities.length !== 1 || quantity.name !== 'quantity' || quantity.type !== 'number' || !details || !['text', 'blank'].includes(mode)) throw new Error('Invalid engraving form');
      const pattern = metadata('_tf_pattern');
      const capped = metadata('_tf_capped');
      if (!pattern || pattern === 'chain' || !['true', 'false'].includes(capped)) throw new Error('Invalid engraving pattern');
      const fields = Array.from(form.querySelectorAll('[data-tf-field]')).map(node => {
        const kind = node.dataset.tfKind;
        const slot = kind === 'front-line' ? node.dataset.tfLine : null;
        if (!['front', 'front-line', 'date', 'initial_1', 'initial_2', 'first_name'].includes(kind) || (kind === 'front-line' && !/^[1-7]$/.test(slot || '')) || !['INPUT', 'TEXTAREA'].includes(node.tagName) || typeof node.value !== 'string' || !node.name) throw new Error('Invalid engraving field');
        return { node, kind, slot, tag: node.tagName, type: node.type, source_name: node.name, value: node.value };
      });
      if (new Set(fields.map(fieldKey)).size !== fields.length) throw new Error('Duplicate engraving slots');
      const front = fields.filter(field => field.kind === 'front' || field.kind === 'front-line');
      if (mode === 'blank' && front.length !== 0) throw new Error('Blank front has editable text');
      if (mode === 'text' && (front.length !== 1 || front[0].kind !== 'front' || front[0].tag !== 'TEXTAREA')) throw new Error('Mismatched front structure');
      return { form, details, quantity, productID, variantID: variants[0].value, pattern, capped, mode, fields };
    });
    if (new Set(modes.map(mode => mode.mode)).size !== 2 || modes.filter(mode => mode.details.open).length > 1) throw new Error('Invalid front choice');
    const first = modes[0];
    if (modes.some(mode => mode.productID !== first.productID || mode.variantID !== first.variantID || mode.pattern !== first.pattern || mode.capped !== first.capped)) throw new Error('Mixed engraving product');
    return { product_id: first.productID, variant_id: first.variantID, pattern: first.pattern, capped: first.capped, modes };
  };
  const save = target => {
    if (!clear()) return null;
    try {
      const product = readProduct();
      const record = {
        schema: 1, captured_at: Date.now(), from_locale: initialLocale, to_locale: target,
        product_id: product.product_id, variant_id: product.variant_id, pattern: product.pattern, capped: product.capped,
        modes: product.modes.map(mode => ({
          mode: mode.mode, open: mode.details.open,
          fields: mode.fields.map(({ node, ...field }) => field),
          quantity: { name: mode.quantity.name, value: mode.quantity.value }
        }))
      };
      const raw = JSON.stringify(record);
      window.sessionStorage.setItem(storageKey, raw);
      savedOnThisPage = true;
      return raw;
    } catch { clear(); return null; }
  };
  const restore = () => {
    const record = take();
    if (!record) return;
    let product;
    let plan;
    try {
      const age = Date.now() - record.captured_at;
      const from = locale(record.from_locale);
      const to = locale(record.to_locale);
      if (record.schema !== 1 || !Number.isSafeInteger(record.captured_at) || age < 0 || age > lifetime || !initialLocale || !from || !to || from !== record.from_locale || to !== record.to_locale || record.from_locale === record.to_locale || record.to_locale !== initialLocale || !Array.isArray(record.modes) || record.modes.length !== 2 || new Set(record.modes.map(mode => mode.mode)).size !== 2 || record.modes.some(mode => typeof mode.open !== 'boolean') || record.modes.filter(mode => mode.open).length > 1) return;
      product = readProduct();
      if (record.product_id !== product.product_id || record.variant_id !== product.variant_id || record.pattern !== product.pattern || record.capped !== product.capped) return;
      plan = product.modes.map(mode => {
        const saved = record.modes.find(item => item.mode === mode.mode);
        if (!saved || !Array.isArray(saved.fields) || saved.fields.length !== mode.fields.length || !saved.quantity || saved.quantity.name !== mode.quantity.name || typeof saved.quantity.value !== 'string') throw new Error('Incomplete mode snapshot');
        const mapped = new Map(saved.fields.map(field => [fieldKey(field), field]));
        if (mapped.size !== saved.fields.length) throw new Error('Duplicate stored slots');
        const fields = mode.fields.map(field => {
          const stored = mapped.get(fieldKey(field));
          if (!stored || stored.kind !== field.kind || stored.slot !== field.slot || stored.tag !== field.tag || stored.type !== field.type || typeof stored.source_name !== 'string' || !stored.source_name || typeof stored.value !== 'string') throw new Error('Changed engraving field');
          return { node: field.node, value: stored.value, before: field.node.value };
        });
        return { mode, fields, value: saved.quantity.value, before: mode.quantity.value, open: saved.open, beforeOpen: mode.details.open };
      });
    } catch { return; }
    try {
      for (const entry of plan) {
        for (const field of entry.fields) field.node.value = field.value;
        entry.mode.quantity.value = entry.value;
      }
      for (const entry of plan) entry.mode.details.open = false;
      for (const entry of plan) if (entry.open) entry.mode.details.open = true;
      if (plan.some(entry => entry.mode.quantity.value !== entry.value || entry.mode.details.open !== entry.open || entry.fields.some(field => field.node.value !== field.value))) throw new Error('Draft values changed');
    } catch {
      for (const entry of plan) {
        for (const field of entry.fields) { try { field.node.value = field.before; } catch {} }
        try { entry.mode.quantity.value = entry.before; } catch {}
        try { entry.mode.details.open = false; } catch {}
      }
      for (const entry of plan) if (entry.beforeOpen) { try { entry.mode.details.open = true; } catch {} }
      return;
    }
    // Reuse the commerce input owner; never carry a checked review into a new language.
    for (const entry of plan) {
      for (const field of entry.fields) field.node.dispatchEvent(new Event('input', { bubbles: true }));
      entry.mode.quantity.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };
  document.addEventListener('change', event => {
    if (event.target !== language) return;
    armed = null;
    const target = locale(language.value);
    if (!initialLocale || !target || target === initialLocale || !Array.from(language.options).some(option => option.value === language.value)) { clear(); return; }
    const intent = { target };
    armed = intent;
    queueMicrotask(() => { if (armed === intent) armed = null; });
  }, true);
  document.addEventListener('submit', event => {
    if (event.target === languageForm) {
      const intent = armed;
      armed = null;
      if (!intent || event.defaultPrevented || locale(language.value) !== intent.target) return;
      const raw = save(intent.target);
      if (raw !== null) queueMicrotask(() => {
        if (!event.defaultPrevented) return;
        try { if (window.sessionStorage.getItem(storageKey) === raw) window.sessionStorage.removeItem(storageKey); } catch {}
      });
    } else if (event.target.matches('[data-tf-engraving-form][data-tf-product-id]') && !event.target.dataset.tfCartKey && !event.target.closest('[data-tf-cart-properties]')) clear();
  });
  // Initial pageshow may follow a just-submitted source-page language change.
  if (document.readyState === 'complete') restore();
  else document.addEventListener('DOMContentLoaded', () => { if (!savedOnThisPage) restore(); }, { once: true });
  window.addEventListener('pageshow', event => { if (event.persisted) restore(); });
})();
